import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaChartBar,
  FaClipboardList,
  FaHome,
  FaTasks,
  FaUsers,
} from "react-icons/fa";
import Navbar from "../../components/Navbar";
import UpcomingSchedule from "./UpcomingSchedule";
import StatusOverview from "./StatusOverview";
import { bookingsAPI } from "../../services/meetingsAPI";
import "./PrivateWorkspaceDashboard.css";

const SIDEBAR_ITEMS = [
  { to: "/meetings/workspace", label: "Home", icon: FaHome, end: true },
  { to: "/meetings/book", label: "Book Room", icon: FaCalendarAlt },
  { to: "/meetings/my-bookings", label: "My Booking", icon: FaClipboardList },
  { to: "/meetings/schedule", label: "Schedule", icon: FaChartBar },
  { to: "/meetings/create-team", label: "Create Your Team", icon: FaUsers },
  { to: "/meetings/status", label: "Status", icon: FaTasks },
];

const ACTION_CARDS = [
  {
    to: "/meetings/book",
    title: "Book Room",
    desc: "Find and book the perfect room for your meetings.",
    cta: "Book Now",
    tone: "blue",
    icon: FaCalendarAlt,
  },
  {
    to: "/meetings/my-bookings",
    title: "My Booking",
    desc: "View and manage all your room bookings.",
    cta: "View Bookings",
    tone: "green",
    icon: FaClipboardList,
  },
  {
    to: "/meetings/schedule",
    title: "Schedule",
    desc: "Check your upcoming meetings and schedules.",
    cta: "View Schedule",
    tone: "purple",
    icon: FaChartBar,
  },
  {
    to: "/meetings/create-team",
    title: "Create Your Team",
    desc: "Create your team and collaborate seamlessly.",
    cta: "Create Team",
    tone: "orange",
    icon: FaUsers,
  },
];

const getUserFirstName = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const name = String(user?.name || user?.staffProfile?.name || user?.email || "Guest").trim();
    return name.split(/\s+/)[0] || "Guest";
  } catch {
    return "Guest";
  }
};

const normalizeStatus = (booking) => {
  const status = String(booking?.status || "").toLowerCase();
  const now = new Date();
  const start = new Date(booking.startTime);
  const end = new Date(booking.endTime);

  if (status === "cancelled") return "cancelled";
  if (status === "pending") return "pending";
  if (status === "completed" || end < now) return "completed";
  if (["approved", "confirmed"].includes(status) && start > now) return "upcoming";
  if (["approved", "confirmed"].includes(status) && start <= now && end >= now) return "upcoming";
  return status || "pending";
};

const PrivateWorkspaceDashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bookingsAPI.getMyBookings();
      setBookings(Array.isArray(res.data) ? res.data : []);
    } catch {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const statusStats = useMemo(() => {
    const counts = { upcoming: 0, completed: 0, cancelled: 0, pending: 0 };
    bookings.forEach((b) => {
      const key = normalizeStatus(b);
      if (counts[key] !== undefined) counts[key] += 1;
    });
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    return { ...counts, total: total || 0 };
  }, [bookings]);

  const firstName = getUserFirstName();

  return (
    <div className="pwd-page meeting-page-with-bottom-nav">
      <Navbar />

      <div className="pwd-layout">
        <aside className="pwd-sidebar" aria-label="Private workspace navigation">
          <nav className="pwd-sidebar-nav">
            {SIDEBAR_ITEMS.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `pwd-sidebar-link${isActive ? " active" : ""}`
                }
              >
                <Icon className="pwd-sidebar-icon" aria-hidden />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="pwd-main">
          <section className="pwd-welcome">
            <div className="pwd-welcome-copy">
              <h1>Welcome back, {firstName}!</h1>
              <p>
                Manage your workspace, bookings, schedule and team effortlessly.
              </p>
            </div>
            <div className="pwd-welcome-art" aria-hidden="true">
              <div className="pwd-art-desk" />
              <div className="pwd-art-chair" />
              <div className="pwd-art-sofa" />
              <div className="pwd-art-lamp" />
              <div className="pwd-art-plant" />
            </div>
          </section>

          <section className="pwd-actions" aria-label="Quick actions">
            {ACTION_CARDS.map(({ to, title, desc, cta, tone, icon: Icon }) => (
              <article key={to} className={`pwd-action-card pwd-action-card--${tone}`}>
                <div className="pwd-action-icon">
                  <Icon aria-hidden />
                </div>
                <h2>{title}</h2>
                <p>{desc}</p>
                <button type="button" className="pwd-action-link" onClick={() => navigate(to)}>
                  {cta} →
                </button>
              </article>
            ))}
          </section>

          <section className="pwd-widgets">
            <UpcomingSchedule bookings={bookings} loading={loading} />
            <StatusOverview stats={statusStats} />
          </section>
        </main>
      </div>
    </div>
  );
};

export default PrivateWorkspaceDashboard;
